import {
  CheckoutShippingParams,
  CheckoutShippingResponse,
  ShippingConfigurationApi,
  ShippingConfigurationListParams,
  ShippingConfigurationPayload,
  ShippingConfigurationsListResponse,
  ShippingAuxOption,
  ShippingNamedItem,
  APPLY_TO_ALL_CURRENCIES,
  SHIPPING_ALL_VALUE,
} from '@/@types/ShippingConfigurations';
import { api } from './api';
import { normalizeCountryToAlpha2 } from '@/utils/shippingCountries';

const BASE = '/v1/shipping-configurations';

function mapCountriesToApi(
  countries: ShippingConfigurationPayload['countries'],
) {
  if (countries?.includes(SHIPPING_ALL_VALUE)) return [SHIPPING_ALL_VALUE];
  return (countries ?? []).map((c) => String(c).toUpperCase());
}

function mapCurrenciesToApi(currencies: string[]) {
  const list = currencies ?? [];
  if (list.includes(APPLY_TO_ALL_CURRENCIES)) {
    return [APPLY_TO_ALL_CURRENCIES];
  }
  return list;
}

function mapCurrenciesFromApi(
  currencies: unknown,
  applyToAll?: unknown,
): string[] {
  if (applyToAll === true || applyToAll === 'true') {
    return [SHIPPING_ALL_VALUE];
  }
  if (!Array.isArray(currencies)) return [];
  if (currencies.includes(APPLY_TO_ALL_CURRENCIES)) {
    return [SHIPPING_ALL_VALUE];
  }
  return currencies.map((c) => String(c));
}

function toApiPayload(payload: ShippingConfigurationPayload) {
  return {
    name: payload.name,
    productIds: payload.products,
    offers: payload.offers,
    currencies: mapCurrenciesToApi(payload.currencies),
    countries: mapCountriesToApi(payload.countries),
    min_quantity: payload.minQuantity,
    amount: payload.amount,
    currency: payload.amountCurrency,
    status: payload.status,
  };
}

function resolveNamedItemId(obj: Record<string, unknown>): string {
  for (const key of ['uuid', 'id', 'value', 'offer_id', 'product_id']) {
    const value = obj[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }

  const nested = obj.product ?? obj.offer;
  if (nested && typeof nested === 'object') {
    return resolveNamedItemId(nested as Record<string, unknown>);
  }

  return '';
}

function resolveNamedItemName(
  obj: Record<string, unknown>,
  fallbackId: string,
): string {
  for (const key of ['name', 'label', 'title']) {
    const value = obj[key];
    if (value != null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return fallbackId;
}

function mapNamedItems(raw: unknown): ShippingNamedItem[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      if (typeof entry === 'string') {
        const id = entry.trim();
        if (!id) return null;
        const isAll = id.toUpperCase() === SHIPPING_ALL_VALUE;
        return {
          id: isAll ? SHIPPING_ALL_VALUE : id,
          name: isAll ? SHIPPING_ALL_VALUE : id,
        };
      }

      if (typeof entry === 'number') {
        const id = String(entry);
        return { id, name: id };
      }

      if (entry && typeof entry === 'object') {
        const obj = entry as Record<string, unknown>;
        const id = resolveNamedItemId(obj);
        if (!id) return null;
        const isAll = id.toUpperCase() === SHIPPING_ALL_VALUE;
        const name = resolveNamedItemName(obj, id);
        return {
          id: isAll ? SHIPPING_ALL_VALUE : id,
          name: isAll ? SHIPPING_ALL_VALUE : name,
        };
      }

      return null;
    })
    .filter((item): item is ShippingNamedItem => !!item);
}

function mergeNamedItemsFromSources(...sources: unknown[]): ShippingNamedItem[] {
  const byId = new Map<string, ShippingNamedItem>();

  for (const source of sources) {
    for (const item of mapNamedItems(source)) {
      const existing = byId.get(item.id);
      const itemHasLabel = item.name !== item.id;
      const existingHasLabel = existing ? existing.name !== existing.id : false;

      if (!existing || (itemHasLabel && !existingHasLabel)) {
        byId.set(item.id, item);
      }
    }
  }

  return Array.from(byId.values());
}

function mapConfiguration(item: ShippingConfigurationApi) {
  const apiItem = item as ShippingConfigurationApi & Record<string, unknown>;

  const products = mergeNamedItemsFromSources(
    apiItem.products,
    apiItem.productIds,
    apiItem.product_ids,
    apiItem.products_ids,
    apiItem.products_uuids,
  );

  const offers = mergeNamedItemsFromSources(
    apiItem.offers,
    apiItem.offerIds,
    apiItem.offer_ids,
  );

  return {
    id: String(item.id),
    name: item.name ?? '',
    products,
    offers,
    currencies: mapCurrenciesFromApi(
      item.currencies,
      (apiItem as { apply_to_all_currencies?: unknown }).apply_to_all_currencies,
    ),
    countries: (item.countries ?? []).map((country) =>
      country === SHIPPING_ALL_VALUE
        ? SHIPPING_ALL_VALUE
        : normalizeCountryToAlpha2(String(country)),
    ),
    minQuantity: (() => {
      const raw =
        (item as unknown as { min_quantity?: unknown })?.min_quantity ??
        (item as unknown as { minQuantity?: unknown })?.minQuantity;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 1;
    })(),
    amount: item.amount,
    amountFormatted:
      item.amount_formatted ??
      (typeof item.amount === 'number'
        ? String(item.amount)
        : ''),
    amountCurrency: item.currency ?? 'BRL',
    status: item.status,
  };
}

function mapOfferOptions(data: unknown): ShippingAuxOption[] {
  const list = Array.isArray(data)
    ? data
    : (data as { data?: unknown[] })?.data ?? [];

  return list.map((item: { id?: string; name?: string }) => ({
    value: String(item.id ?? ''),
    label: String(item.name ?? item.id ?? ''),
  }));
}

function mapCurrencyOptions(data: unknown): ShippingAuxOption[] {
  const list = Array.isArray(data)
    ? data
    : (data as { data?: unknown[] })?.data ?? [];

  return list.map((item: { code?: string; name?: string } | string) => {
    if (typeof item === 'string') {
      return { value: item, label: item };
    }
    const code = String(item.code ?? '');
    return {
      value: code,
      label: item.name ? `${code} — ${item.name}` : code,
    };
  });
}

export class ShippingConfigurationsService {
  static async getProducts(query?: string): Promise<ShippingAuxOption[]> {
    const params = new URLSearchParams();
    if (query) params.append('q', query);

    const response = await api.get(`${BASE}/products?${params.toString()}`);
    return mapOfferOptions(response.data);
  }

  static async getOffersByProductIds({
    productIds,
    q,
    allProducts = false,
  }: {
    productIds: string[];
    q?: string;
    allProducts?: boolean;
  }): Promise<ShippingAuxOption[]> {
    if (!allProducts && !productIds.length) return [];

    const params = new URLSearchParams();
    if (!allProducts) {
      params.append('products', productIds.join(','));
    }
    if (q) params.append('q', q);

    const response = await api.get(`${BASE}/offers?${params.toString()}`);
    return mapOfferOptions(response.data);
  }

  static async getCurrencies(): Promise<ShippingAuxOption[]> {
    const response = await api.get(`${BASE}/currencies`);
    return mapCurrencyOptions(response.data);
  }

  static async list(
    params: ShippingConfigurationListParams = {},
  ): Promise<ShippingConfigurationsListResponse> {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', String(params.page));
    if (params.limit) searchParams.append('limit', String(params.limit));
    if (params.offerId) searchParams.append('offerId', params.offerId);
    if (params.currency) searchParams.append('currency', params.currency);
    if (params.countryId != null) {
      searchParams.append('countryId', String(params.countryId));
    }
    if (params.status != null) {
      searchParams.append('status', String(params.status));
    }

    const query = searchParams.toString();
    const response = await api.get(`${BASE}${query ? `?${query}` : ''}`);

    const body = response.data as {
      data?: ShippingConfigurationApi[];
      meta?: ShippingConfigurationsListResponse['meta'];
      links?: ShippingConfigurationsListResponse['links'];
    };

    return {
      data: (body.data ?? []).map(mapConfiguration),
      meta: body.meta,
      links: body.links,
    };
  }

  static async getById(id: number) {
    const response = await api.get(`${BASE}/${id}`);
    const item =
      (response.data as { data?: ShippingConfigurationApi })?.data ??
      (response.data as ShippingConfigurationApi);
    return mapConfiguration(item);
  }

  static async create(payload: ShippingConfigurationPayload) {
    const response = await api.post(BASE, toApiPayload(payload));
    const item =
      (response.data as { data?: ShippingConfigurationApi })?.data ??
      (response.data as ShippingConfigurationApi);
    return mapConfiguration(item);
  }

  static async update(
    id: string,
    payload: Partial<ShippingConfigurationPayload>,
  ) {
    const body: Record<string, unknown> = {};
    if (payload.name != null) body.name = payload.name;
    if (payload.products != null) body.productIds = payload.products;
    if (payload.offers != null) body.offers = payload.offers;
    if (payload.currencies != null) {
      body.currencies = mapCurrenciesToApi(payload.currencies);
    }
    if (payload.countries != null) {
      body.countries = mapCountriesToApi(payload.countries);
    }
    if (payload.minQuantity != null) body.min_quantity = payload.minQuantity;
    if (payload.amount != null) body.amount = payload.amount;
    if (payload.amountCurrency != null) {
      body.currency = payload.amountCurrency;
    }
    if (payload.status != null) body.status = payload.status;

    const response = await api.patch(`${BASE}/${id}`, body);
    const item =
      (response.data as { data?: ShippingConfigurationApi })?.data ??
      (response.data as ShippingConfigurationApi);
    return mapConfiguration(item);
  }

  static async delete(id: string) {
    await api.delete(`${BASE}/${id}`);
  }

  static async resolveCheckoutShipping(
    params: CheckoutShippingParams,
  ): Promise<CheckoutShippingResponse> {
    const searchParams = new URLSearchParams({
      offerId: params.offerId,
      currency: params.currency,
      countryId: String(params.countryId),
      quantity: String(params.quantity),
    });

    const response = await api.get(
      `/v1/checkout/shipping?${searchParams.toString()}`,
    );
    return (
      (response.data as { data?: CheckoutShippingResponse })?.data ??
      (response.data as CheckoutShippingResponse)
    );
  }
}
